import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { loginPassword, sendOtp, verifyOtp } from '../api/auth';
import { authStore } from '../api/client';
import LoginNavHeader from '../components/auth/LoginNavHeader';
import LoginShowcasePanel from '../components/auth/LoginShowcasePanel';

function landingPathForRole(role) {
  if (role === 'CANDIDATE') return '/candidate';
  return '/dashboard';
}

function OtpInput({ value, onChange, length = 6, disabled }) {
  const inputsRef = useRef([]);

  const digits = useMemo(() => {
    const arr = (value || '').split('').slice(0, length);
    while (arr.length < length) arr.push('');
    return arr;
  }, [value, length]);

  const setDigit = (idx, char) => {
    const next = digits.slice();
    next[idx] = char;
    onChange(next.join(''));
  };

  const handleChange = (idx, e) => {
    const raw = e.target.value;
    const onlyDigits = raw.replace(/\D/g, '');
    if (!onlyDigits) {
      setDigit(idx, '');
      return;
    }

    if (onlyDigits.length > 1) {
      const next = digits.slice();
      let i = idx;
      for (const ch of onlyDigits) {
        if (i >= length) break;
        next[i] = ch;
        i++;
      }
      onChange(next.join(''));
      const focusIdx = Math.min(i, length - 1);
      inputsRef.current[focusIdx]?.focus();
      return;
    }

    setDigit(idx, onlyDigits);
    if (idx < length - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      if (digits[idx]) {
        setDigit(idx, '');
      } else if (idx > 0) {
        inputsRef.current[idx - 1]?.focus();
        setDigit(idx - 1, '');
      }
      e.preventDefault();
    }
  };

  const handlePaste = (idx, e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!text) return;
    e.preventDefault();
    const next = digits.slice();
    let i = idx;
    for (const ch of text) {
      if (i >= length) break;
      next[i] = ch;
      i++;
    }
    onChange(next.join(''));
    const focusIdx = Math.min(i, length - 1);
    inputsRef.current[focusIdx]?.focus();
  };

  return (
    <div className="flex justify-between gap-2">
      {digits.map((digit, idx) => (
        <input
          key={idx}
          ref={(el) => { inputsRef.current[idx] = el; }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={(e) => handleChange(idx, e)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={(e) => handlePaste(idx, e)}
          className="login-form-input !w-11 !min-h-[48px] !px-0 text-center text-lg font-semibold"
          aria-label={`Digit ${idx + 1} of ${length}`}
        />
      ))}
    </div>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();

  const [mode, setMode] = useState('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add('route-login');
    return () => document.documentElement.classList.remove('route-login');
  }, []);

  useEffect(() => {
    if (authStore.isAuthenticated) {
      navigate(landingPathForRole(authStore.role), { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const finishLogin = useCallback((authResponse) => {
    authStore.set(authResponse);
    const nextPath = landingPathForRole(authResponse.user?.role);
    navigate(nextPath, { replace: true });
  }, [navigate]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setLoading(true);
    try {
      const res = await loginPassword({
        email: email.trim(),
        password,
      });
      finishLogin(res.data);
    } catch (err) {
      setError(err.message || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!otpEmail.trim()) {
      setError('Please enter your email address first.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await sendOtp({ email: otpEmail.trim() });
      setOtpSent(true);
      setOtpCode('');
      setResendIn(60);
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      setError('Please enter the 6-digit code.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await verifyOtp({ email: otpEmail.trim(), code: otpCode });
      finishLogin(res.data);
    } catch (err) {
      setError(err.message || 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  const switchToOtp = () => {
    setMode('otp');
    setError('');
    if (email && !otpEmail) setOtpEmail(email);
  };

  const switchToPassword = () => {
    setMode('password');
    setError('');
  };

  return (
    <>
      <LoginNavHeader />
      <div className="login-page">
        <LoginShowcasePanel />

        <div className="login-form-panel flex-1 flex flex-col min-h-0 overflow-y-auto">
          <div className="flex-1 flex items-center justify-center px-5 sm:px-8 py-8 login-page__safe-top login-page__safe-bottom">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-[420px]"
            >
              <div className="login-form-card rounded-2xl p-7 sm:p-9">
                <div className="mb-7">
                  <h1 className="text-[1.65rem] sm:text-[1.75rem] font-semibold login-text-primary tracking-tight mb-2">
                    Welcome back
                  </h1>
                  <p className="text-sm login-text-secondary leading-relaxed">
                    {mode === 'password'
                      ? 'Sign in with your work email to open the Samastix dashboard.'
                      : 'Enter the one-time code sent to your email.'}
                  </p>
                </div>

                {error && (
                  <div className="mb-4 login-error" role="alert">
                    {error}
                  </div>
                )}

                {mode === 'password' ? (
                  <form onSubmit={handlePasswordSubmit} className="space-y-4">
                    <label className="block">
                      <span className="text-xs font-medium login-text-secondary mb-1.5 block">
                        Work email
                      </span>
                      <input
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        className="login-form-input"
                        required
                      />
                    </label>

                    <label className="block">
                      <span className="text-xs font-medium login-text-secondary mb-1.5 block">
                        Password
                      </span>
                      <input
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="login-form-input"
                        required
                      />
                      <div className="mt-1.5 text-right">
                        <button
                          type="button"
                          onClick={switchToOtp}
                          className="text-xs login-text-accent hover:underline border-0 bg-transparent cursor-pointer p-0"
                        >
                          Forgot password? Use OTP sign-in
                        </button>
                      </div>
                    </label>

                    <button
                      type="submit"
                      disabled={loading}
                      className="login-btn-primary w-full py-3.5 text-sm flex items-center justify-center gap-2 cursor-pointer min-h-[48px] border-0 mt-2"
                    >
                      {loading ? 'Signing in…' : 'Sign in'}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyOtp} className="space-y-4">
                    <label className="block">
                      <span className="text-xs font-medium login-text-secondary mb-1.5 block">
                        Email
                      </span>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          autoComplete="email"
                          value={otpEmail}
                          onChange={(e) => setOtpEmail(e.target.value)}
                          placeholder="you@company.com"
                          className="login-form-input flex-1"
                          required
                        />
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={loading || resendIn > 0}
                          className="shrink-0 px-4 py-2 rounded-full border border-slate-200 bg-white text-sm font-medium login-text-secondary hover:bg-slate-50 disabled:opacity-50"
                        >
                          {resendIn > 0 ? `Resend (${resendIn}s)` : otpSent ? 'Resend' : 'Send code'}
                        </button>
                      </div>
                    </label>

                    {otpSent && (
                      <div>
                        <span className="text-xs font-medium login-text-secondary mb-2 block">
                          Enter the 6-digit code sent to your email
                        </span>
                        <OtpInput value={otpCode} onChange={setOtpCode} disabled={loading} />
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading || !otpSent || otpCode.length !== 6}
                      className="login-btn-primary w-full py-3.5 text-sm flex items-center justify-center cursor-pointer min-h-[48px] border-0 mt-2 disabled:opacity-60"
                    >
                      {loading ? 'Verifying…' : 'Verify and continue'}
                    </button>

                    <div className="text-center pt-1">
                      <button
                        type="button"
                        onClick={switchToPassword}
                        className="text-xs login-text-accent hover:underline border-0 bg-transparent cursor-pointer p-0"
                      >
                        Back to password sign-in
                      </button>
                    </div>
                  </form>
                )}

                <p className="mt-6 pt-6 border-t border-slate-200/60 text-center text-sm login-text-muted">
                  Don&apos;t have access yet?{' '}
                  <Link to="/register" className="login-text-accent hover:underline font-medium no-underline">
                    Register
                  </Link>
                </p>
              </div>

              <p className="mt-5 text-center text-[11px] login-text-faint">
                Protected by enterprise-grade security.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}
